import React from 'react';
import {
  Button,
  Tag,
  Skeleton,
} from 'antd';
import {
  SettingOutlined,
  CloseOutlined,
  FolderOpenOutlined,
  LogoutOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { WorkspaceTree } from './WorkspaceTree';
import { useAuthStore } from '../../store/auth';
import { Workspace } from '../../types';
import { useBuildInfo } from '../../hooks/useBuildInfo';
import { BUILD_INFO } from '../../build-info.generated';
import { APP_NAME } from '../../constants';
import { useProfilePicture } from '../../hooks/useProfilePicture';

interface SavedState {
  id: string;
  name: string;
  timestamp: Date;
  workspaceId: string;
}

interface LeftNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceSelect: (workspace: Workspace) => void;
  onSavedStateRestore: (workspace: Workspace, savedState: SavedState) => void;
  onSettingsClick: () => void;
}

export const LeftNavigation: React.FC<LeftNavigationProps> = ({
  isOpen,
  onClose,
  onWorkspaceSelect,
  onSavedStateRestore,
  onSettingsClick,
}) => {
  const { user, signOut } = useAuthStore();
  const { buildInfo, loading: buildInfoLoading } = useBuildInfo();
  const { profilePicture, isLoading: isLoadingPicture } = useProfilePicture();

  // API environment info from build info endpoint
  const apiIsProduction = buildInfo?.environment === 'production';
  
  // Web app environment info from build-time generated info
  const webIsProduction = BUILD_INFO.environment === 'production';
  
  const environmentInfo = {
    // API info
    apiName: apiIsProduction ? 'PROD' : 'DEV',
    apiType: apiIsProduction ? 'red' : 'blue',
    apiBuildNumber: buildInfo?.buildNumber || 0,
    
    // Web app info  
    webName: webIsProduction ? 'PROD' : 'DEV',
    webType: webIsProduction ? 'red' : 'blue',
    webBuildNumber: BUILD_INFO.buildNumber
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, // No header, start from top
        left: 0,
        width: '256px',
        height: '100vh', // Full height
        backgroundColor: 'var(--color-bg-primary)',
        //borderRight: '1px solid var(--color-border-secondary)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header with app title and close button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.5rem 1rem',
        //borderBottom: '1px solid var(--color-border-secondary)',
        backgroundColor: 'var(--color-primary)', // Primary color
        height: '40px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AppstoreOutlined style={{ fontSize: '20px', color: 'var(--color-bg-primary)' }} />
          <h3 style={{ 
            margin: 0, 
            fontSize: '1rem',
            fontWeight: '600',
            color: 'var(--color-bg-primary)'
          }}>
            {APP_NAME}
          </h3>
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: 'var(--color-bg-primary)' }}
        />
      </div>

      {/* Workspaces Section Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        //borderBottom: '1px solid var(--color-border-secondary)',
        backgroundColor: 'var(--color-bg-primary)',
      }}>
        <span style={{
          fontSize: '0.875rem',
          fontWeight: 'bold',
          color: 'var(--color-text-primary)',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
          WORKSPACES
        </span>
        <FolderOpenOutlined style={{ fontSize: '16px', opacity: 0.6 }} />
      </div>

      {/* Workspace Tree */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <WorkspaceTree
          onWorkspaceSelect={onWorkspaceSelect}
          onSavedStateRestore={onSavedStateRestore}
        />
      </div>

      {/* Bottom section - Settings and User */}
      <div style={{
        //borderTop: '1px solid var(--color-border-secondary)',
        padding: '0.5rem',
        marginTop: 'auto',
      }}>
        {/* Settings */}
        <div 
          onClick={onSettingsClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            marginBottom: '0.5rem',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: 'var(--color-text-primary)',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span>SETTINGS</span>
          <SettingOutlined style={{ fontSize: '16px', opacity: 0.6 }} />
        </div>

        {/* Sign Out */}
        <div 
          onClick={handleSignOut}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            marginBottom: '0.5rem',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: 'var(--color-warning)',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span>SIGN OUT</span>
          <LogoutOutlined style={{ fontSize: '16px', color: 'var(--color-warning)' }} />
        </div>

        {/* User Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          backgroundColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              color: 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.name || 'User'}
            </div>
            <div style={{
              fontSize: '0.675rem',
              color: 'var(--color-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.email}
            </div>
          </div>
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--sfc-white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.625rem',
              fontWeight: '600',
              marginLeft: '0.5rem',
              flexShrink: 0,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {profilePicture && !isLoadingPicture ? (
              <img 
                src={profilePicture} 
                alt="Profile" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }} 
                onError={() => {
                  console.log('Navigation profile picture failed to load');
                }}
              />
            ) : (
              user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'
            )}
          </div>
        </div>

        {/* Build Information - 3 Badges */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0.5rem',
          gap: '0.25rem'
        }}>
          {buildInfoLoading ? (
            <Skeleton.Input active size="small" style={{ width: '60px' }} />
          ) : (
            <>
              {/* Badge 1: Web Environment & Build */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: environmentInfo.webType === 'red' ? 'var(--color-error)' : 'var(--color-primary)',
                color: 'var(--color-bg-primary)',
                padding: '3px 6px',
                borderRadius: '4px',
                fontSize: '0.6875rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap'
              }}>
                WEB {environmentInfo.webName} #{environmentInfo.webBuildNumber}
              </div>

              {/* Badge 2: API Environment & Build */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: environmentInfo.apiType === 'red' ? 'var(--color-error)' : 'var(--color-primary)',
                color: 'var(--color-bg-primary)',
                padding: '3px 6px',
                borderRadius: '4px',
                fontSize: '0.6875rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap'
              }}>
                API {environmentInfo.apiName} #{environmentInfo.apiBuildNumber}
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
};
